<template>
  <div class="circle-rewards">
    <div class="head">
      <div class="title" :class="locale">
        {{ $t('monopoly.circleRewards') }}
      </div>
      <div class="tag">
        {{ $t('monopoly.circleAccumulated', { count: totalLaps }) }}
      </div>
    </div>

    <div class="body">
      <div class="items">
        <div
          class="item"
          v-for="item in displayList"
          :key="item.rewardCode || item.targetLaps"
          :class="{
            claimable: item.status === STATUS_CLAIMABLE,
            claimed: item.status === STATUS_CLAIMED,
          }"
        >
          <div
            class="chest-wrap"
            @click="handlePreview(item)"
          >
            <div class="chest-bg"></div>
            <img
              class="chest"
              src="@/views/act/monopoly/assets/img/baoxiang.png"
              alt="chest"
            >
          </div>
          <div class="laps">
            {{ $t('monopoly.circleLaps', { count: item.targetLaps }) }}
          </div>
        </div>
      </div>

      <div class="progress-wrap">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: `${progressPercent}%` }"></div>
        </div>
      </div>
    </div>

    <RewardDialog
      v-model="previewVisible"
      :title="previewTitle"
      :rewards="previewRewards"
    />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import MonopolyApi from '@/views/act/monopoly/api'
import RewardDialog from '../RewardDialog/index.vue'

const STATUS_LOCKED = 0
const STATUS_CLAIMABLE = 1
const STATUS_CLAIMED = 2

const store = useStore()
const { locale, t } = useI18n()
const activityInfo = inject('monopolyActivityInfo')
const commonConfig = inject('monopolyCommonConfig')
const registerLapRewardListRefresh = inject('registerMonopolyLapRewardListRefresh', () => {})

const list = ref([])
const totalLaps = ref(0)
const previewVisible = ref(false)
const previewRewards = ref([])
const previewTitle = ref('')

const isLoggedIn = computed(() => !!store.state.actUserInfo?.token)

const displayList = computed(() => {
  if (list.value.length) return list.value
  return (commonConfig?.lapRewards || []).map((item) => ({
    ...item,
    status: STATUS_LOCKED,
  }))
})

const progressPercent = computed(() => {
  if(totalLaps.value >= 30) return 100
  if (totalLaps.value > 20 && totalLaps.value < 30) return 84
  if (totalLaps.value > 10 && totalLaps.value < 20) return 66.5
  if (totalLaps.value > 5 && totalLaps.value < 10) return 50
  const lapsMap = {
    0: 0,
    1: 9,
    2: 18,
    3: 25,
    4: 33.5,
    5: 42,
    6: 50,
    10: 58,
    11: 66.5,
    20: 75,
    21: 84,
    30: 100
  }
  return lapsMap[totalLaps.value] || 0
})

const syncTotalLapsFromActivity = () => {
  if (typeof activityInfo?.totalLaps === 'number') {
    totalLaps.value = activityInfo.totalLaps
  }
}

const loadLapRewardList = async () => {
  if (!isLoggedIn.value) {
    list.value = (commonConfig?.lapRewards || []).map((item) => ({
      ...item,
      status: STATUS_LOCKED,
    }))
    totalLaps.value = 0
    return
  }
  try {
    const res = await MonopolyApi.getLapRewardList()
    list.value = res?.list || []
    totalLaps.value = res?.totalLaps ?? activityInfo?.totalLaps ?? 0
  } catch (e) {
    console.error(e)
    syncTotalLapsFromActivity()
  }
}

// 一键领取后先本地标已领，再拉接口校准
const markClaimableAsClaimed = () => {
  if (!list.value.length) return
  list.value = list.value.map((item) => (
    item.status === STATUS_CLAIMABLE
      ? { ...item, status: STATUS_CLAIMED }
      : item
  ))
}

const refreshAfterExtract = async () => {
  markClaimableAsClaimed()
  await loadLapRewardList()
}

// 点击宝箱：仅展示该档奖励说明，不触发领取（领取改由「领取奖励」按钮一并完成）
const handlePreview = (item) => {
  previewTitle.value = t('monopoly.circleLaps', { count: item.targetLaps })
  previewRewards.value = item.props || []
  previewVisible.value = true
}

watch(
  () => store.state.actUserInfo?.token,
  () => {
    loadLapRewardList()
  },
  { immediate: true }
)

watch(
  () => activityInfo?.totalLaps,
  () => {
    syncTotalLapsFromActivity()
    if (isLoggedIn.value) {
      loadLapRewardList()
    }
  }
)

watch(
  () => commonConfig?.lapRewards,
  () => {
    if (!list.value.length) {
      loadLapRewardList()
    }
  }
)

onMounted(() => {
  registerLapRewardListRefresh(refreshAfterExtract)
})

onUnmounted(() => {
  registerLapRewardListRefresh(null)
})
</script>

<style lang="less" scoped>
.circle-rewards {
  width: 750px;
  height: 318px;
  margin: 20px auto 0;
  background: url('~@/views/act/monopoly/assets/img/circle-rewards-bg.png') no-repeat center center / 100% 100%;
  box-sizing: border-box;

  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    height: 75px;
    padding-left: 15px;
    padding-top: 15px;
    box-sizing: border-box;

    .title {
      font-weight: bold;
      font-size: 26px;
      color: #fff;
      &.id {
        font-size: 22px;
      }
    }

    .tag {
      line-height: 40px;
      height: 40px;
      padding: 0 10px 0 15px;
      background: #FFEB5D;
      border: 1px solid #FFEB5D;
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
      color: #9E3B06;
      font-size: 22px;
      white-space: nowrap;
    }
  }

  .body {
    // position: relative;
    box-sizing: border-box;
    height: 230px;
    // background-color: rgba(0, 0, 0, 0.5);
    overflow: hidden;
    margin-top: 10px;
  }

  .progress-wrap {
    width: 730px;
    height: 24px;
    margin: 0 auto;
    margin-top: 15px;
    border: 2px solid #929292;
    border-radius: 12px;
  }

  .progress-track {
    width: 100%;
    height: 20px;
    background: #cfd8e6;
    border-radius: 12px;
  }

  .progress-fill {
    height: 100%;
    height: 20px;
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
    background: linear-gradient(to right, #FACA10, #FBF896);
    transition: width 0.3s ease;
    position: relative;
    &::after {
      content: '';
      position: absolute;
      width: 6px;
      height: 28px;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      background: #fff;
    }
  }

  .items {
    position: relative;
    z-index: 2;
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 15px;
  }

  .item {
    width: 110px;
    text-align: center;

    .chest-wrap {
      position: relative;
      width: 108px;
      height: 108px;
      margin: 0 auto;
      cursor: pointer;
    }

    .chest-bg {
      width: 100%;
      height: 100%;
      background: url('~@/views/act/monopoly/assets/img/circle-item-bg.png') no-repeat center center / 100% 100%;
    }

    .chest {
      position: absolute;
      left: 50%;
      top: 50%;
      width: 80px;
      height: auto;
      transform: translate(-50%, -52%);
      pointer-events: none;
    }

    .laps {
      margin-top: 8px;
      font-size: 24px;
      font-weight: bold;
      color: #1a4d9c;
      line-height: 1.2;
    }

    &.claimable {
      .chest-bg {
        background-image: url('~@/views/act/monopoly/assets/img/circle-item-bg-active.png');
      }
    }

    &.claimed {
      .chest-wrap {
        filter: grayscale(100%);
      }

      .laps {
        color: #7a8aa3;
      }
    }
  }
}
</style>
